import { CompanyRef } from '../../shared/services/current-user.service';

export interface GeoLocation {
  latitude: number;
  longitude: number;
}

export interface TimeInterval {
  open: string; // "HH:mm" 24-hour, e.g. "08:00"
  close: string; // "HH:mm" 24-hour, e.g. "12:00"
}

export type Weekday =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

// A day absent from the map (or with an empty array) means "closed".
export type WorkingHours = Partial<Record<Weekday, TimeInterval[]>>;

export interface Location extends CompanyRef {
  id: string;
  name: string;
  streetName: string;
  postalCode?: string;
  city: string;
  order: number;
  phone?: string;
  email?: string;
  website?: string;
  description?: string;
  avatarUrl?: string;
  avatarThumbnailUrl?: string;
  geoLocation?: GeoLocation;
  workingHours?: WorkingHours;
  /** false: disabled, customers can't send orders here. Missing means active. */
  active?: boolean;
}

export const isActive = (location: Pick<Location, 'active'>): boolean => location.active !== false;

/** Editable location fields: id, order, company and active status are set by LocationsService. */
export type LocationInput = Omit<Location, 'id' | 'order' | 'active' | keyof CompanyRef>;

export const WEEKDAYS: { key: Weekday; label: string }[] = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

/**
 * Single-line address, e.g. "Main St, 51000 Rijeka". Empty parts are dropped
 * (no dangling commas). `includeName` prefixes the location name (map search).
 */
export function formatAddress(
  location: Partial<
    Pick<Location, 'name' | 'streetName' | 'postalCode' | 'city'>
  >,
  options: { includeName?: boolean } = {},
): string {
  const cityLine = [location.postalCode, location.city]
    .filter(Boolean)
    .join(' ');
  const parts = [location.streetName, cityLine].filter(Boolean);
  if (options.includeName && location.name) {
    parts.unshift(location.name);
  }
  return parts.join(', ');
}

/** Google Maps `q` value: exact coords when geocoded, else a name+address string. */
export function locationMapQuery(
  location: Pick<
    Location,
    'name' | 'streetName' | 'postalCode' | 'city' | 'geoLocation'
  >,
): string {
  if (location.geoLocation) {
    return `${location.geoLocation.latitude},${location.geoLocation.longitude}`;
  }
  return formatAddress(location, { includeName: true });
}

/** "08:00–12:00" */
export function formatInterval(interval: TimeInterval): string {
  return `${interval.open}–${interval.close}`;
}

/** "08:00–12:00, 14:00–20:00"; "" when the day has no intervals. */
export function formatDayHours(intervals: TimeInterval[] | undefined): string {
  return (intervals ?? []).map(formatInterval).join(', ');
}
